import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  ICrossTestMetricSelection,
  ISelectOption,
  IUser,
} from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IComparisonPopulationSetting } from '../comparisonPopulationSettingsProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface IProfileReportSettings {
  metrics: ICrossTestMetricSelection[];
}

/**
 * @description the subset of the profile settings that are not template specific
 */
interface IProfileReportSettingOptions {
  targetTimeframe: IDateRange;
  graph: ISelectOption<string>;
  comparisonPopulation: IComparisonPopulationSetting;
}

/**
 * @description the complete document in the database
 */
interface IProfileReportSettingEntity extends IProfileReportSettingOptions {
  selected: ISettingsTemplate<IProfileReportSettings>;
}

/**
 * @description the interface that is emitted by the provider
 */
export interface IProfileReportSettingsState
  extends IProfileReportSettingOptions {
  selected: ISettingsTemplate<IProfileReportSettings>;
  templates: Array<ISettingsTemplate<IProfileReportSettings>>;
}

const defaultSettings: IProfileReportSettings = {
  metrics: [],
};

/**
 * @description observes the profile report settings and related settings templates
 */
export const profileReportSettingsProvider =
  (): Observable<IProfileReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap(
        (
          user: IUser
        ): Observable<
          [
            ISettingsTemplate<IProfileReportSettings>,
            IProfileReportSettingOptions,
            Array<ISettingsTemplate<IProfileReportSettings>>
          ]
        > => {
          const listenerArgs = {
            userId: user.id,
            firestore: user.services.firestore,
          };
          return combineLatest([
            selectedTemplateProvider(listenerArgs),
            profileSettingsOptionsProvider(listenerArgs),
            templatesProvider(listenerArgs),
          ]);
        }
      ),
      map(
        (
          result: [
            ISettingsTemplate<IProfileReportSettings>,
            IProfileReportSettingOptions,
            Array<ISettingsTemplate<IProfileReportSettings>>
          ]
        ): IProfileReportSettingsState => {
          const [selected, options, templates] = result;
          if (selected?.id) {
            return {
              selected,
              templates,
              ...options,
            };
          } else {
            return {
              selected: null,
              templates,
              ...options,
            };
          }
        }
      )
    );
  };

interface IListenerArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: IListenerArgs
): Observable<ISettingsTemplate<IProfileReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/profileReport`
    ) as DocumentReference<IProfileReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IProfileReportSettingEntity
      ): ISettingsTemplate<IProfileReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IProfileReportSettings>,
        curr: ISettingsTemplate<IProfileReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes changes the subset of the profile settings that are not template specific
 * @param userId
 * @returns
 */
const profileSettingsOptionsProvider = (
  args: IListenerArgs
): Observable<IProfileReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/profileReport`
    ) as DocumentReference<IProfileReportSettingEntity>
  ).pipe(
    map(
      (entity: IProfileReportSettingEntity): IProfileReportSettingOptions => ({
        targetTimeframe: entity?.targetTimeframe,
        graph: entity?.graph,
        comparisonPopulation: entity?.comparisonPopulation,
      })
    ),
    distinctUntilChanged(
      (
        prev: IProfileReportSettingOptions,
        curr: IProfileReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: IListenerArgs
): Observable<Array<ISettingsTemplate<IProfileReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/profileReport/templates`
      ) as CollectionReference<ISettingsTemplate<IProfileReportSettings>>,
      where('active', '==', true)
    )
  );
};
